<template>
  <BaseDialog v-model="dialog" maxWidth="1000" title="Tactic">
    <div class="tactic-info">
      <table>
        <tr v-for="(_, i) in fields.filter((_, i) => i % 2 === 0)" :key="i">
          <td class="info-title">{{ fields[i * 2].title }}</td>
          <td class="info-value">
            <span v-if="fields[i * 2].format">
              {{ fields[i * 2].format(tactic[fields[i * 2].key]) }}
            </span>
            <span v-else> {{ tactic[fields[i * 2].key] }}</span>
          </td>
          <td class="info-title" v-if="i * 2 + 1 < fields.length">
            {{ fields[i * 2 + 1].title }}
          </td>
          <td class="info-title" v-else />
          <td class="info-value" v-if="i * 2 + 1 < fields.length">
            <span v-if="fields[i * 2 + 1].format">
              {{ fields[i * 2 + 1].format(tactic[fields[i * 2 + 1].key]) }}
            </span>
            <span v-else> {{ tactic[fields[i * 2 + 1].key] }}</span>
          </td>
          <td class="info-value" v-else />
        </tr>
      </table>
    </div>
    <v-card-title>Operations</v-card-title>
    <div class="operation-info">
      <table>
        <tr>
          <th v-for="field in tacticOperationFields" :key="field.key">
            {{ field.title }}
          </th>
          <th>Cost</th>
        </tr>
        <tr
          v-for="tacticOperation in tactic.tactic_operations"
          :key="tacticOperation.id"
        >
          <td v-for="field in tacticOperationFields" :key="field.key">
            {{ tacticOperation[field.key] }}
          </td>
          <td>
            <NumberField
              class="width-120"
              :model-value="tacticOperation.cost.toNumber()"
              text-align="left"
              label="Cost"
              prefix="$"
              variant="outlined"
              density="compact"
              hide-details
              required
              @update:modelValue="tacticOperation.cost = new Decimal($event)"
            />
          </td>
        </tr>
      </table>
    </div>

    <template #actions>
      <v-btn :loading="loading" @click="save">Save</v-btn>
    </template>
  </BaseDialog>
</template>

<script setup lang="ts">
import { ref } from "vue";
import BaseDialog from "../base/dialogs/BaseDialog.vue";
import { DateObject, Tactic, TacticOperation } from "@/classes";
import NumberField from "../base/inputs/NumberField.vue";
import Decimal from "decimal.js";

const emits = defineEmits(["change"]);

const dialog = ref(false);
const tactic = ref<Tactic>(new Tactic());
const loading = ref(false);

const open = async (id: string | null) => {
  dialog.value = true;
  if (id) {
    tactic.value = await Tactic.get(id);
  } else {
    tactic.value = new Tactic();
  }
};

const fields: {
  title: string;
  key: keyof Tactic;
  format?: (val: any) => string;
}[] = [
  { title: "Maintenance Item", key: "maintenance_item" },
  { title: "Maintenance Item Desc.", key: "maintenance_item_description" },
  { title: "Maintenance Plan", key: "maintenance_plan" },
  { title: "Maintenance Plan Desc.", key: "maintenance_plan_description" },
  { title: "Strategy", key: "strategy" },
  { title: "Functional Location", key: "functional_location" },
  { title: "Func. Loc. Desc.", key: "functional_location_description" },
  { title: "Asset Criticality", key: "asset_criticality" },
  { title: "Planning Plant", key: "maintenance_item_planning_plant" },
  { title: "Planner Group", key: "maintenance_item_planner_group" },
  { title: "Main Work Center", key: "maintenance_item_main_work_center" },
  {
    title: "Main Work Center Desc.",
    key: "maintenance_item_main_work_center_description",
  },
  { title: "Activity Type", key: "activity_type" },
  { title: "Activity Type Desc.", key: "activity_type_description" },
  { title: "Task List", key: "task_list" },
  { title: "Task List Header Dec.", key: "task_list_header_description" },
];

const tacticOperationFields: {
  title: string;
  key: keyof TacticOperation;
  format?: (val: any) => string;
}[] = [
  { title: "Operation Number", key: "operation_number" },
  { title: "Maintenance Package", key: "maintenance_package" },
  { title: "Operation Text", key: "operation_text" },
  { title: "Operation Work Center", key: "operation_work_center" },
  { title: "Operation Planning Plant", key: "operation_planning_plant" },
];

const save = async () => {
  loading.value = true;
  await tactic.value.save();
  loading.value = false;
  dialog.value = false;
  emits("change");
};

defineExpose({ open });
</script>

<style lang="scss" scoped>
.tactic-info {
  display: flex;
  table {
    width: 100%;
    border-collapse: collapse;
  }
  td,
  th {
    text-align: left;
    border: 1px solid #dddddd;
    padding: 8px;
  }
  tr:nth-child(even) {
    background-color: #eeeeee;
  }

  .info-title {
    text-align: right;
    font-weight: 600;
  }
  .info-value {
    text-align: left;
  }
}
.operation-info {
  display: flex;
  margin-top: 8px;
  table {
    width: 100%;
    border-collapse: collapse;
  }

  td {
    border: 1px solid #dddddd;
    padding: 8px;
    text-align: left;
  }

  tr:nth-child(odd) {
    td {
      background-color: #eeeeee;
    }
  }
  th {
    background-color: #dddddd;
    border: 1px solid #cccccc;
    padding: 8px;
    text-align: left;
  }
}
</style>
